import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fabric } from 'fabric';
import { combineLatest, distinctUntilChanged, map, Subscription, take } from 'rxjs';
import {
  BoardFolder,
  Frame,
  FramePrivacy,
  FrameType,
  ROOT_BOARD_FOLDER_NAME,
  Session,
  SessionUser,
} from 'src/app/models/session';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import {
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { SessionsVptService } from 'src/app/services/sessions-vpt.service';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { SpaceCommentsRepository } from 'src/app/state/space-comments.repository';
import { SpaceRepository } from 'src/app/state/space.repository';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { SpaceLeaderModeService } from 'src/app/services/space-leader-mode.service';
import { User } from 'src/app/models/user';
import { PresenceProvider } from 'src/app/services/presence-provider';
import {
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
  NotificationActionButton,
} from 'src/app/services/notification-toaster.service';
import { IconBackground } from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { INFOS } from 'src/app/common/utils/notification-constants';
import { getTruncatedName } from 'src/app/common/utils/common-util';
import { SessionOverlayModalsService } from 'src/app/services/session-overlay-modals.service';
import { v4 as uuidv4 } from 'uuid';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FollowModeUiService } from 'src/app/services/leader-follow-modes/follow-mode/follow-mode-ui.service';
import { ChildModeService } from 'src/app/services/child-mode.service';
import { KeyboardShortcutsCheckService } from 'src/app/services/keyboard-shortcuts-check.service';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { WbCreatePrivateBoardModalComponent } from '../wb-create-private-board-modal/wb-create-private-board-modal.component';
import { SpaceBoardFoldersService } from '../../../services/space-board-folders.service';
import { PanelView } from '../../panel/panel.component';
import { WbUsersFrameAccessModalComponent } from '../wb-users-frame-access-modal/wb-users-frame-access-modal.component';
import { WbChangeBoardBackgroundModalComponent } from '../wb-change-board-background-modal/wb-change-board-background-modal.component';

import { YSessionBoardItemsAdapter } from '../../common/y-session-board-items-adapter.service';
import { isBoardWebViewer } from '../../common/y-session';
import { FabricObject } from '../items-canvas/board-items/board-items-manager';
import { cloneFabricObject, ensureAllObjectAreLive } from '../wb-canvas/fabric-utils';
import { BoardItemsInteractionsManager } from '../items-canvas/board-items-interactions-manager';

const FRAMES_TRAY_ELEMENT_ID = 'frames-tray';

export function scrollFramesTrayToBottom(): void {
  modifiedSetTimeout(() => {
    const tray = document.getElementById(FRAMES_TRAY_ELEMENT_ID);
    if (!tray) {
      return;
    }
    requestAnimationFrame(() => {
      tray.scrollTop = tray.scrollHeight;
    });
  });
}

enum KeyDownActions {
  OpenAllBoards = 'b',
  GoNextBoard = 'n',
  GoPrevBoard = 'p',
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class BoardOperationsService {
  frameCanvasItemsSubscription?: Subscription;
  prevRect?: fabric.Rect;
  currentFrameUid?: string;
  domListener = this.domListenerFactoryService.createInstance();

  constructor(
    private spaceBoardsService: SpaceBoardsService,
    private spaceBoardFoldersService: SpaceBoardFoldersService,
    private spaceRepo: SpaceRepository,
    private commentsRepo: SpaceCommentsRepository,
    private realtimeSpaceService: RealtimeSpaceService,
    private sessionsVptService: SessionsVptService,
    private sharedDataService: SessionSharedDataService,
    private modalManagerService: ModalManagerService,
    private flagsService: FlagsService,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private translate: TranslateService,
    private deviceAndBrowserDecoratorService: DeviceAndBrowserDetectorService,
    private presenceProvider: PresenceProvider,
    private notificationToasterService: NotificationToasterService,
    private ySessionAdapter: YSessionBoardItemsAdapter,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private sessionOverlayModalsService: SessionOverlayModalsService,
    private domListenerFactoryService: DomListenerFactoryService,
    private deviceDetectorService: DeviceDetectorService,
    private followModeUiService: FollowModeUiService,
    private keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
    private childModeService: ChildModeService,
  ) {}

  isBoardDuplicationEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_BOARD_DUPLICATE);
  canChangeBoardBackgrounds = this.flagsService.isFlagEnabled(FLAGS.SPACES_BOARDS_BACKGROUNDS);
  canChangeFramePrivacy = this.flagsService.isFlagEnabled(FLAGS.SPACES_PRIVATE_BOARDS);
  isToolbarShortcutEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_TOOLBAR_SHORTCUTS);

  canGoNext$ = combineLatest([
    this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards$(),
    this.spaceBoardsService.activeSpaceSelectedBoardIndex$,
  ]).pipe(
    map(([frames, currentFrameIndex]) => currentFrameIndex + 1 < frames.length),
    distinctUntilChanged(),
  );

  canGoBack$ = this.spaceBoardsService.activeSpaceSelectedBoardIndex$.pipe(
    map((currentFrameIndex) => currentFrameIndex > 0),
    distinctUntilChanged(),
  );

  public changeFrameBG(frame: Frame): void {
    const changeBackgroundModalParams = {
      panelClass: 'zero-padding-dialog',
      disableClose: true,
      data: {
        sessionId: this.spaceRepo.activeSpaceId,
        roomId: this.spaceRepo.activeSpaceCurrentRoomUid,
        frame,
      },
    };
    this.modalManagerService.showModal(
      WbChangeBoardBackgroundModalComponent,
      changeBackgroundModalParams,
    );
  }

  public duplicateFrame(
    frame: Frame,
    canChangeFrame = false,
    privacySettings: {
      privateUsers?: string[];
      privacy?: FramePrivacy;
      boardFolderUid?: string;
      name?: string;
    } = {},
    frameProperties: {
      name?: string;
    } = {},
    showAccessDialog = false,
    onComplete?: () => void,
  ): Frame | undefined {
    const roomId = this.spaceRepo.activeSpaceCurrentRoomUid || '';

    if (!this.spaceRepo.activeSpace) {
      return;
    }

    const sessionId = this.spaceRepo.activeSpace._id;

    const newFrame = new Frame({
      name: `${frame?.name} ${this.translate.instant('copy')}`,
      frameType: frame?.frameType ?? FrameType.WHITEBOARD,
      backgroundPattern: frame?.backgroundPattern,
      backgroundColor: frame?.backgroundColor,
      boardFolderUid: this.spaceRepo.activeSpace.selectedFolderBoardUid || ROOT_BOARD_FOLDER_NAME,
      ...privacySettings,
      ...frameProperties,
    });

    const afterClosed = (output: any) => {
      if (!output) {
        return;
      }

      this.realtimeSpaceService.service.addFrame(newFrame, sessionId, roomId);

      if (frame && frame.uid) {
        this.ySessionAdapter
          .decodedBoardItems$(sessionId, frame.uid)
          .pipe(take(1))
          .subscribe(({ objects }) => {
            if (!objects) {
              return;
            }
            ensureAllObjectAreLive(objects, (liveObjects) => {
              this.ySessionAdapter.addToYSession(
                sessionId,
                liveObjects.map((object) => this.cloneFabricObjectToNewBoard(object, newFrame.uid)),
              );
            });
          });
      }

      if (canChangeFrame && this.spaceRepo.activeSpace) {
        const framesLength =
          this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards().length;
        this.selectFrame(
          this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards()[framesLength - 1].uid,
        );
      }
      onComplete?.();
      return newFrame;
    };

    if (privacySettings?.privacy === FramePrivacy.PRIVATE && showAccessDialog) {
      this.openCreatePrivateBoardModal(
        newFrame,
        { isModalForDuplicateBoard: true },
        { afterClosed: afterClosed.bind(this) },
      );
    } else {
      afterClosed(true);
    }
  }

  /**
   * Create private boards for each participant
   * @param frameType - Frame type to create
   * @param participants
   * @param shouldDuplicateExistingBoard - if true, we duplicate the selectedFrame for each participant, otherwise we create a new private board for each participant
   * @param selectedFrame - selected frame to be duplicated. Mandatory if shouldDuplicateExistingBoard
   * @param canChangeFrame
   * @returns
   */
  public createPrivateBoardPerParticipant(
    frameType: FrameType = FrameType.WHITEBOARD,
    participants: SessionUser[],
    shouldDuplicateExistingBoard: boolean,
    selectedFrame?: Frame,
    canChangeFrame?: boolean,
  ) {
    const roomId = this.spaceRepo.activeSpaceCurrentRoomUid;
    const sessionId = this.spaceRepo.activeSpace?._id;
    if (!sessionId || !roomId) {
      return;
    }

    const usersMap = this.createUsersMap();

    // Create frames for each participant
    const frames = participants
      .map((participant) => usersMap[participant._id])
      .sort(this.compareParticipants)
      .map((user) =>
        this.createFrameForUser(user, {
          frameType,
          shouldDuplicateExistingBoard,
          selectedFrame,
          canChangeFrame,
        }),
      )
      .filter((frame): frame is Frame => frame !== undefined && frame.validate());

    this.realtimeSpaceService.service.addFrames(frames, sessionId, roomId);
  }

  private createFrameForUser(
    user: User | undefined,
    options: {
      frameType: FrameType;
      shouldDuplicateExistingBoard: boolean;
      selectedFrame?: Frame;
      canChangeFrame?: boolean;
    },
  ): Frame | undefined {
    if (!user) {
      return undefined;
    }

    const boardName = this.translate.instant("{{name}}'s board", {
      name: user?.name || user?.email,
    });

    if (options.shouldDuplicateExistingBoard && options.selectedFrame) {
      return this.duplicateFrame(
        options.selectedFrame,
        options.canChangeFrame,
        { privacy: FramePrivacy.PRIVATE, privateUsers: [user._id] },
        { name: boardName },
      );
    }

    return new Frame({
      name: boardName,
      frameType: options.frameType,
      backgroundPattern: this.spaceRepo.activeSpace?.sessionDefaultFramesBackground?.pattern,
      backgroundColor: this.spaceRepo.activeSpace?.sessionDefaultFramesBackground?.color,
      privacy: FramePrivacy.PRIVATE,
      privateUsers: [user._id],
      boardFolderUid: this.spaceRepo.activeSpace?.selectedFolderBoardUid || ROOT_BOARD_FOLDER_NAME,
    });
  }

  private createUsersMap(): { [key: string]: User } {
    return (
      this.spaceRepo.activeSpace?.populatedUsers?.reduce<{ [key: string]: User }>(
        (prev, curr) => ({ ...prev, [curr._id]: curr }),
        {},
      ) || {}
    );
  }

  /**
   * compare users for the sort function when creating private frame for each user
   * @param userA
   * @param userB
   */
  private compareParticipants(userA: User, userB: User) {
    const nameA = userA?.name;
    const emailA = userA?.email;

    const nameB = userB?.name;
    const emailB = userB?.email;

    // Compare names if available, otherwise compare emails
    return (nameA || emailA).localeCompare(nameB || emailB);
  }

  public cloneFabricObjectToNewBoard(object: FabricObject, boardId: string) {
    const clonedObject = cloneFabricObject(object);

    clonedObject.boardUid = boardId;
    clonedObject.uid = uuidv4();
    clonedObject.vuid = uuidv4();

    // This was added to replace the session with a new one later in the web viewer component
    // This was made to avoid the duplication of ids when duplicating boards
    // And that is because we have a uniqueness constraint in the backend on
    // loadedFromSessionId, which is dependent on this id
    if (isBoardWebViewer(clonedObject)) {
      clonedObject.configurations.hyperbeamSessionId = undefined;
      clonedObject.configurations.embedUrl = undefined;
      clonedObject.configurations.remoteControlToken = undefined;
    }

    return clonedObject;
  }

  moveFrameToFolder(frame: Frame, folder: BoardFolder): void {
    // clicking the same frame
    if (
      frame.boardFolderUid === folder.uid ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      return;
    }
    frame.boardFolderUid = folder.uid;
    this.realtimeSpaceService.service.updateFrameBoardFolder(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );

    // for active frame, activate related board folder
    if (this.currentFrameUid === frame.uid) {
      this.spaceBoardFoldersService.updateActiveBoardFolderForSelectedFrame(frame);
    }
  }

  removeBoardFromBoardFolder(frame: Frame) {
    if (!this.spaceRepo.activeSpaceId || !this.spaceRepo.activeSpaceCurrentRoomUid) {
      return;
    }

    if (frame.boardFolderUid) {
      frame.boardFolderUid = undefined;
      this.realtimeSpaceService.service.updateFrameBoardFolder(
        frame,
        this.spaceRepo.activeSpaceId,
        this.spaceRepo.activeSpaceCurrentRoomUid,
      );

      // for active frame, activate related board folder
      if (this.currentFrameUid === frame.uid) {
        this.spaceBoardFoldersService.updateActiveBoardFolderForSelectedFrame(frame);
      }
    }
  }

  /**
   * select the frame ( board ) and render its content ( canvas )
   * @param uid
   */
  selectFrame(uid: string): void {
    const newFrameToSelect = this.spaceBoardsService
      .getActiveSpaceCurrentRoomAccessibleBoards()
      .find((frame) => frame.uid === uid);

    const frameUidInUserFrames = newFrameToSelect?.uid;
    const currentFrameUid = this.spaceBoardsService.activeSpaceSelectedBoard?.uid;
    // This is here to ensure that we don't load the canvas without needing to
    if (!frameUidInUserFrames || currentFrameUid === frameUidInUserFrames) {
      return;
    }

    // board switch not initial board selection on entering space
    if (currentFrameUid) {
      this.commentsRepo.updateCommentsState({
        switchingToBoard: uid,
      });
    }

    this.sessionsVptService.storeViewportTransform(currentFrameUid);
    this.currentFrameUid = uid;

    this.spaceBoardFoldersService.updateActiveBoardFolderForSelectedFrame(newFrameToSelect);
    /**
     * on changing tabs, we need to discard active objects to prevent the context menu
     * or the object to appear on the next board
     */
    this.boardItemsInteractionsManager.triggerDeselectAll();
    this.sharedDataService.fabricCanvas?.fire('closeActiveText');
    this.spaceBoardsService.setActiveSpaceSelectedBoard(uid);
    this.sessionsVptService.loadViewportTransform(uid);
    this.setUpResetToOriginIfEmptySubscription();
    if (
      !this.spaceLeaderModeService.currentUserIsBeingLed() &&
      !this.followModeUiService.isFollowing() &&
      !this.sessionOverlayModalsService.isGVCallNotConnectedOverlayModalShown()
    ) {
      this.sharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
    }
  }

  /**
   * subscribe to canvas items only once when a new frame is selected to check if there is any elements or items in the viewport
   * and based on that we can reset canvas to origin or not
   */
  setUpResetToOriginIfEmptySubscription() {
    if (!this.spaceRepo.activeSpace) {
      return;
    }

    if (this.frameCanvasItemsSubscription) {
      this.frameCanvasItemsSubscription.unsubscribe();
    }

    const frame = this.spaceBoardsService.activeSpaceSelectedBoard;
    if (!frame) {
      return;
    }

    const elementsObservable = this.ySessionAdapter.decodedBoardItems$(
      this.spaceRepo.activeSpace._id,
      frame.uid,
    );

    if (!elementsObservable) {
      return;
    }

    this.frameCanvasItemsSubscription = elementsObservable
      .pipe(untilDestroyed(this))
      .pipe(take(1))
      .subscribe((value) => {
        // if no objects in the current viewport and no leader mode available we reset canvas to origin
        if (
          !this.spaceLeaderModeService.currentUserIsBeingLed() &&
          !this.sessionsVptService.areAnyObjectsVisibleInViewport(value.objects) &&
          !this.followModeUiService.isFollowing()
        ) {
          this.sessionsVptService.resetCanvasToOrigin();
        }
      });
  }

  addPrevRectangle() {
    if (this.prevRect) {
      this.removePrevRect();
    }
    const previewRect = new fabric.Rect({
      top: 0,
      left: 0,
      width: 1920,
      height: (1 / Math.sqrt(2)) * 1920,
      fill: 'rgba(223,223,232,0.3)',
      strokeWidth: 0,
      hasControls: false,
      selectable: false,
      type: 'prevRect',
      excludeFromExport: true,
      evented: false,
    });
    previewRect.hoverCursor = 'default';
    this.prevRect = previewRect;
    this.sharedDataService.fabricCanvas?.add(previewRect);
    this.sharedDataService.fabricCanvas?.sendToBack(previewRect);
    this.sharedDataService.fabricCanvas?.requestRenderAll();
  }

  removePrevRect() {
    if (this.prevRect) {
      this.sharedDataService.fabricCanvas?.remove(this.prevRect);
      this.sharedDataService.fabricCanvas?.requestRenderAll();
    }
  }

  toggleLockBoard(frame: Frame): void {
    if (
      !this.spaceRepo.activeSpace ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      return;
    }
    frame.locked = !frame.locked;
    this.realtimeSpaceService.service.updateFrameLockState(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );
  }

  clearFrameAnnotations(frame: Frame) {
    if (
      !this.spaceRepo.activeSpace ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      return;
    }
    this.realtimeSpaceService.service.clearFrameAnnotations(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );
    this.sharedDataService.clearFrameAnnotations$.next(frame.uid);
  }

  clearFrameContent(frame: Frame) {
    if (
      !this.spaceRepo.activeSpace ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      return;
    }
    this.realtimeSpaceService.service.clearFrameContent(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );
    this.sharedDataService.clearFrameContent$.next(frame.uid);
  }

  openChangeAccessDialog(frame: Frame, params = {}, modalOptions = {}): void {
    if (!this.spaceRepo.activeSpaceId || !this.spaceRepo.activeSpaceCurrentRoomUid) {
      return;
    }
    // should open the modal to change frame privacy
    const framePrivacyModalParams = {
      panelClass: 'frame-privacy-dialog',
      data: {
        sessionId: this.spaceRepo.activeSpaceId,
        roomId: this.spaceRepo.activeSpaceCurrentRoomUid,
        frame,
        ...params,
      },
      disableClose: true,
    };
    this.modalManagerService.showModal(
      WbUsersFrameAccessModalComponent,
      framePrivacyModalParams,
      modalOptions,
    );
  }

  openCreatePrivateBoardModal(frame: Frame, params = {}, modalOptions = {}): void {
    if (!this.spaceRepo.activeSpaceId || !this.spaceRepo.activeSpaceCurrentRoomUid) {
      return;
    }

    const createPrivateBoardModalParams = {
      panelClass: 'create-private-board-dialog',
      data: {
        sessionId: this.spaceRepo.activeSpaceId,
        roomId: this.spaceRepo.activeSpaceCurrentRoomUid,
        frame,
        ...params,
      },
      disableClose: true,
    };
    this.modalManagerService.showModal(
      WbCreatePrivateBoardModalComponent,
      createPrivateBoardModalParams,
      modalOptions,
    );
  }

  /**
   * Helper method to select the next frame to navigate to after deletion
   * @param frames Array of frames to select from
   * @param deletedFrameIndex Index of the frame being deleted
   * @returns UID of the next frame to select, or undefined if no frames available
   */
  private selectNextFrameFromArray(frames: Frame[], deletedFrameIndex: number): string | undefined {
    if (deletedFrameIndex >= frames.length - 1) {
      // Deleted frame was the last frame, select the previous frame
      return frames[deletedFrameIndex - 1]?.uid;
    } else {
      // Select the frame that will be at the same index after deletion (currently at index + 1)
      // It will handle the case where the deleted frame is the frist frame in the folder as well
      return frames[deletedFrameIndex + 1]?.uid;
    }
  }

  /**
   * Finds next frame when current frame is being deleted locally by the user
   */
  getNextFrameAfterLocalDeletion(
    deletedFrameUid: string,
    deletedFrameIndex: number,
  ): string | undefined {
    const allAccessibleFrames = this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards();
    const currentFolderUid = this.spaceBoardFoldersService.activeSpaceSelectedBoardFolderUid;
    const framesInCurrentFolder = this.spaceBoardFoldersService.getFramesInFolder(
      currentFolderUid || ROOT_BOARD_FOLDER_NAME,
      allAccessibleFrames,
    );

    // If there are frames in the current folder, prioritize selecting from the same folder
    if (framesInCurrentFolder.length > 1) {
      // Need at least 2 frames to have an alternative after deletion
      const deletedFrameIndexInFolder = framesInCurrentFolder.findIndex(
        (frame) => frame.uid === deletedFrameUid,
      );

      if (deletedFrameIndexInFolder !== -1) {
        // Frame was in the current folder, use helper method to select next frame
        return this.selectNextFrameFromArray(framesInCurrentFolder, deletedFrameIndexInFolder);
      }
    }

    // Fallback to selecting from all accessible frames using the same helper method
    return this.selectNextFrameFromArray(allAccessibleFrames, deletedFrameIndex);
  }

  /**
   * Finds next frame when current frame is no longer accessible
   * Handles both remote deletion and privacy changes
   */
  getNextFrameAfterFrameLoss(
    lostFrameUid: string,
    allFrames: Frame[],
    accessibleFrames: Frame[],
    currentFolderUid?: string,
    currentFrameIndex?: number,
  ): string | null {
    if (accessibleFrames.length === 0) {
      return null;
    }

    // Try to determine the folder context for better selection
    let targetFolderUid = currentFolderUid;

    // If we can still find the lost frame in allFrames, use its folder
    const lostFrame = allFrames.find((frame) => frame.uid === lostFrameUid);
    if (lostFrame?.boardFolderUid) {
      targetFolderUid = lostFrame.boardFolderUid;
    }

    // Try to stay in the same folder
    if (targetFolderUid) {
      const framesInFolder = this.spaceBoardFoldersService.getFramesInFolder(
        targetFolderUid,
        accessibleFrames,
      );

      if (framesInFolder.length > 0) {
        return framesInFolder[0].uid;
      }
    }

    // Fallback: use the same pattern as existing code to maintain position
    if (currentFrameIndex !== undefined && currentFrameIndex >= 0) {
      const adjustedIndex =
        currentFrameIndex >= accessibleFrames.length
          ? accessibleFrames.length - 1
          : currentFrameIndex;

      return accessibleFrames[adjustedIndex].uid;
    }

    // Final fallback: first accessible frame
    return accessibleFrames[0].uid;
  }

  deleteFrame(frameToDeleteUid: string): void {
    if (
      !this.spaceRepo.activeSpace ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      return;
    }

    const deletedFrameIndex = this.spaceBoardsService
      .getActiveSpaceCurrentRoomAccessibleBoards()
      .findIndex((f) => f.uid === frameToDeleteUid);
    // if another user has already deleted the frame return
    const frame =
      this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards()[deletedFrameIndex];

    if (!frame) {
      return;
    }

    // Calculate the next frame to select BEFORE deleting the frame
    // so that getNextFrameAfterDeletion can still find the frame in the folder
    const nextFrameUid = this.getNextFrameAfterLocalDeletion(frameToDeleteUid, deletedFrameIndex);

    this.boardItemsInteractionsManager.triggerDeselectAll();
    this.realtimeSpaceService.service.removeFrame(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );

    const remainingFrames = this.spaceBoardsService
      .getActiveSpaceCurrentRoomAccessibleBoards()
      .filter((f) => f.uid !== frameToDeleteUid);

    if (remainingFrames.length === 0) {
      return;
    }

    // Update selected frame.
    // Find frame corresponding to this.currentFrameUid
    const currentFrame = remainingFrames.find(
      (sessionFrame) => sessionFrame.uid === this.currentFrameUid,
    );

    // if current frame is deleted successfully, use the next frame to select
    const nextFrameToSelectUid = currentFrame?.uid ?? nextFrameUid;

    if (nextFrameToSelectUid) {
      this.selectFrame(nextFrameToSelectUid);
    }
  }

  openBoardsTab(config: { preventCloseAllBoardsWhenItsOpen?: boolean } = {}): void {
    if (config.preventCloseAllBoardsWhenItsOpen) {
      const { panelView, expanded } = this.sharedDataService.leftPanelView.getValue() ?? {
        panelView: undefined,
        expanded: false,
      };
      if (
        this.sharedDataService.changeLeftPanelView.getValue() === PanelView.tabs &&
        panelView === PanelView.tabs &&
        expanded
      ) {
        return;
      }
    }
    this.keyboardShortcutsCheckService.closeOpenMenus();
    this.sharedDataService.changeLeftPanelView.next(PanelView.tabs);
  }

  navigateToPreviousBoard(): void {
    const currentFrameIndex = this.spaceBoardsService.activeSpaceSelectedBoardIndex;
    this.navigateToIndex(currentFrameIndex - 1);
  }

  navigateToNextBoard(): void {
    const currentFrameIndex = this.spaceBoardsService.activeSpaceSelectedBoardIndex;
    this.navigateToIndex(currentFrameIndex + 1);
  }

  navigateToIndex(index: number): void {
    const frames = this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards();
    if (index < 0 || index >= frames.length) {
      return;
    }

    this.selectFrame(frames[index].uid);
  }

  private handleKeyDownAction(action: KeyDownActions): void {
    switch (action) {
      case KeyDownActions.GoNextBoard:
        this.navigateToNextBoard();
        break;
      case KeyDownActions.GoPrevBoard:
        this.navigateToPreviousBoard();
        break;
      case KeyDownActions.OpenAllBoards:
        this.openBoardsTab();
        break;
    }
  }

  initKeyDownActionsListners(): void {
    if (!this.isToolbarShortcutEnabled || this.deviceDetectorService.isMobile()) {
      return;
    }

    this.clearDomListener();

    // On macOS, alt.Dead is the key for (alt + n) because it adds an accent (˜)
    // Pressing it alone doesn’t insert a character immediately,
    // it waits for the next key (n) to combine the two and produce ñ
    const macOsShortcuts = ['alt.∫', 'alt.π', 'alt.Dead'];
    const windowsShortcuts = ['alt.b', 'alt.p', 'alt.n'];
    const shortcuts = this.deviceAndBrowserDecoratorService.isMacOS()
      ? macOsShortcuts
      : windowsShortcuts;

    shortcuts.forEach((shortcut) => {
      this.domListener.add(
        document,
        `keydown.${shortcut}`,
        (e: KeyboardEvent) => {
          if (this.childModeService.isChildModeActive) {
            return;
          }
          if (this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcuts(e)) {
            return;
          }
          // use e.code instead of e.key as it is more reliable across OS/keyboard layouts
          const key = e.code.toLowerCase().replace('key', '');
          // Only check leader mode for navigation shortcuts (prev/next board: 'p' and 'n')
          if (key === KeyDownActions.GoPrevBoard || key === KeyDownActions.GoNextBoard) {
            if (this.spaceLeaderModeService.currentUserIsBeingLed()) {
              return;
            }
          }
          this.handleKeyDownAction(key as KeyDownActions);
        },
        true,
      );
    });
  }

  clearDomListener(): void {
    this.domListener.clear();
  }

  addFrameToCurrentRoom(
    frameType: FrameType = FrameType.WHITEBOARD,
    folderId?: string,
  ): Frame | null {
    if (!this.spaceRepo.activeSpace || !this.spaceRepo.activeSpaceCurrentRoomUid) {
      return null;
    }

    const frame = new Frame({
      // use totalFramesCount in naming to make sure that we don't have duplicated names
      // because of the private board access
      name: `${this.translate.instant('Board')} ${
        this.spaceBoardsService.activeSpaceCurrentRoomFrames.length + 1
      }`,
      frameType: frameType,
      backgroundPattern: this.spaceRepo.activeSpace.sessionDefaultFramesBackground?.pattern,
      backgroundColor: this.spaceRepo.activeSpace.sessionDefaultFramesBackground?.color,
      boardFolderUid: folderId,
    });

    this.realtimeSpaceService.service.addFrame(
      frame,
      this.spaceRepo.activeSpace._id,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );

    return frame;
  }

  public insertNewFrameAndSelectIt() {
    const boardIdToRenderIn = this.addFrameToCurrentRoom()?.uid;
    boardIdToRenderIn && this.selectFrame(boardIdToRenderIn);
  }

  createNewFolderInCurrentRoom(folderName: string): BoardFolder {
    const folder = new BoardFolder({
      name: folderName,
      roomUid: this.spaceRepo.activeSpaceCurrentRoomUid as string,
    });
    this.realtimeSpaceService.service.addBoardFolder(
      this.spaceRepo.activeSpaceId as string,
      folder,
    );

    return folder;
  }

  /**
   * Gets the list of active users who don't have access to a private frame
   * @param frame The frame to check
   * @returns Array of users without access
   */
  private getNonAccessUsers(frame: Frame): User[] {
    const spaceId = this.spaceRepo.activeSpaceId;
    const roomId = this.spaceRepo.activeSpaceCurrentRoomUid;
    if (!spaceId || !roomId) {
      return [];
    }

    const { activeUsers } = this.presenceProvider.getSpaceUsersActivityStatus(spaceId, roomId);
    return activeUsers.filter((user) => {
      // Private users include frame's privateUsers and the owner of the space
      const isOwner = Session.isOwnedByUser(this.spaceRepo.activeSpace, user);
      const hasAccess = frame.privateUsers?.includes(user._id) || isOwner;
      return !hasAccess;
    });
  }

  /**
   * Checks if a private board notification should be shown when selecting a frame
   * @param frame The frame to check
   * @returns True if the notification should be shown, false otherwise
   */
  private shouldShowPrivateBoardNotification(frame: Frame): boolean {
    // Check if the frame is private
    if (frame.privacy !== FramePrivacy.PRIVATE) {
      return false;
    }

    // Check if leader mode is on and the current user is the leader
    const leaderModeState = this.spaceLeaderModeService.getSpaceLeaderModeStateUi();
    if (!leaderModeState.leaderModeEnabled || !leaderModeState.currentUserIsLeader) {
      return false;
    }

    // Check if there are active users who aren't part of the frame's private users
    const nonAccessUsers = this.getNonAccessUsers(frame);

    // Return true if there are active users who don't have access to the frame
    return nonAccessUsers.length > 0;
  }

  /**
   * Shows a notification when a leader selects a private board while there are active users who don't have access
   * @param frame The private frame selected
   */
  private showPrivateBoardNotification(frame: Frame): void {
    const nonAccessUsers = this.getNonAccessUsers(frame);
    if (nonAccessUsers.length === 0) {
      return;
    }

    // Build the notification title
    let displayTitle: string;
    if (nonAccessUsers.length === 1) {
      const username = getTruncatedName(nonAccessUsers[0].name || nonAccessUsers[0].email);
      displayTitle = this.translate.instant('{{username}} cannot access this private board', {
        username,
      });
    } else if (nonAccessUsers.length === 2) {
      const username1 = getTruncatedName(nonAccessUsers[0].name || nonAccessUsers[0].email);
      const username2 = getTruncatedName(nonAccessUsers[1].name || nonAccessUsers[1].email);
      displayTitle = this.translate.instant(
        '{{username1}} and {{username2}} cannot access this private board',
        {
          username1,
          username2,
        },
      );
    } else {
      const username = getTruncatedName(nonAccessUsers[0].name || nonAccessUsers[0].email);
      displayTitle = this.translate.instant(
        '{{username}} and {{count}} others cannot access this private board',
        {
          username,
          count: nonAccessUsers.length - 1,
        },
      );
    }

    // Create action button
    const notificationPrimaryAction: NotificationActionButton = {
      text: this.translate.instant('Change board access'),
      icon: 'access_menu_icon_white',
      iconConfig: {
        type: IconTypes.svgIcon,
      },
      onClick: () => {
        this.openChangeAccessDialog(frame);
      },
      close: true,
    };

    // Configure icon
    const notificationIcon: NotificationIconConfig = {
      assetName: 'incognito-mode-black',
      type: NotificationIconTypes.SVG_ICON,
      width: 18,
      height: 18,
      background: IconBackground.INFO,
    };

    // Build and show notification
    const notificationData = new V2NotificationDataBuilder(INFOS.PRIVATE_BOARD_WITH_ACTIVE_USERS)
      .style(ToasterPopupStyle.WARN)
      .type(NotificationType.WARNING)
      .title(displayTitle)
      .description(this.translate.instant('This board is private, visible to selected people.'))
      .icon(notificationIcon)
      .actionButtonsConfig({ primary: notificationPrimaryAction })
      .timeOut(20)
      .showProgressBar(true)
      .dismissable(true)
      .build();

    this.notificationToasterService.showNotification(notificationData);
  }

  /**
   * Dismisses the private board notification
   */
  private dismissPrivateBoardNotification(): void {
    this.notificationToasterService.dismissNotificationsByCode([
      INFOS.PRIVATE_BOARD_WITH_ACTIVE_USERS,
    ]);
  }

  /**
   * Checks if a frame is private
   * @param frame The frame to check
   * @returns True if the frame is private, false otherwise
   */
  isFramePrivate(frame: Frame): boolean {
    return frame.privacy === FramePrivacy.PRIVATE;
  }

  /**
   * Checks if a notification about private board access should be shown
   * and shows/dismisses it as appropriate based on the given frame
   * @param frame The frame to check
   */
  checkAndHandlePrivateBoardNotification(frame: Frame): void {
    if (this.shouldShowPrivateBoardNotification(frame)) {
      this.showPrivateBoardNotification(frame);
      return;
    }

    // Dismiss the notification if the frame is not private
    if (!this.isFramePrivate(frame)) {
      this.dismissPrivateBoardNotification();
    }
  }
}
